import React, { useEffect, useState } from 'react';
import { Grid, Typography, Autocomplete, TextField, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  assignPhysicianAction,
  assignSupervisorAction,
  getAllSupervisiorByDiscipline,
  resetSubmissionEventAction,
} from 'src/redux/actions/documentationAction';
import { getAllPhysicians } from 'src/redux/actions/patientAction';
import loader from 'src/components/LoaderImg/loader.gif';

type Props = {
  close: any;
  activeTab: string;
  selectedDocument: any;
  role: string;
  source?: 'FROM_DSN' | '' | 'OTHER_SECTION';
  DSNId?: string;
};

const SelectSuperVisor = (props: Props) => {
  const { close, activeTab, selectedDocument, role, source = '', DSNId } = props;
  const isPhysicianRole = role === 'Physician';
  // THIN-7350: the supervisor list must follow the *document's* discipline, not the acting
  // user's. Dashboard callers carry it on selectedDocument.type (same pattern as the Re-assign
  // modal); the EOC/DSN callers thread it through activeTab. Prefer the document's discipline
  // and fall back to activeTab so every caller resolves to the note's actual discipline.
  const documentDiscipline = selectedDocument?.type || activeTab;
  // THIN-7231: physician selection needs the physician list (GET /provider/physician/list),
  // not the supervisor-by-discipline list. Read from the appropriate reducer per role and
  // dispatch the matching fetch in the effect below.
  const supervisorsResponse = useSelector((state: any) => state.getAllSupervisorsReducer);
  const physiciansResponse = useSelector((state: any) => state.getEnterprisePhysicianReducer);
  // THIN-7231 follow-up: GET /provider/physician/list is paginated and the reducer replaces
  // enterprisePhysician on each page, so accumulate pages into a local list and feed the
  // dropdown from it. Supervisor options arrive in one shot and are left unchanged.
  const [physicianList, setPhysicianList] = useState<any[]>([]);
  const [pageNo, setPageNo] = useState(0);
  const [physicianSearch, setPhysicianSearch] = useState('');
  const physicianContent = physiciansResponse?.enterprisePhysician?.content;
  const physicianIsLast = physiciansResponse?.enterprisePhysician?.last;

  const options: any[] = isPhysicianRole
    ? physicianList
    : (supervisorsResponse?.response?.data ?? []);
  const optionsLoading = isPhysicianRole
    ? physiciansResponse?.loading
    : supervisorsResponse?.loading;
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedData, setSelectedData] = useState<any>(null);
  const checkAddSupervisorResponse = useSelector((state: any) => state.callSubmitEventReducer);

  const dispatch = useDispatch();
  const [dsnUuid, setDsnUuid] = useState<string>('');
  useEffect(() => {
    if(!DSNId) return;
    setDsnUuid(DSNId);
  }, [DSNId]);

  const handleChangeSupervisor = (_e: any, value: any) => {
    setSelectedData(value);
  };

  const handleSearchInputChange = (value: string, reason: string) => {
    // Supervisor options are filtered client-side by MUI; only the physician list is
    // searched server-side. Ignore MUI's programmatic resets (e.g. fired on selection).
    if (!isPhysicianRole || reason === 'reset') return;
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPageNo(0);
      setPhysicianSearch(value);
    }, 1000);

    setSearchTimeout(timeout);
  };

  const loadMorePhysicians = () => {
    // Standard scroll-to-bottom infinite scroll: advance the page until the API says it's the
    // last one. A search returns only its first page of matches (mirrors customer-edit-form).
    if (!physicianIsLast && physicianSearch === '') {
      setPageNo((prev) => prev + 1);
    }
  };

  // THIN-7231: facilityId is a required query param on GET /provider/physician/list.
  // toFormSubmissionsV2 on the EOC endpoint doesn't project facilityId onto the document
  // DTO today, so fall back to localStorage (the canonical FE source for the current
  // facility context — same pattern used by customer-edit-form). Refetches on page/search
  // change so scroll-to-load-more and server-side search both work.
  useEffect(() => {
    if (!isPhysicianRole) return;
    const facilityId = selectedDocument?.facilityId ?? localStorage.getItem('facilityId');
    dispatch(
      getAllPhysicians({
        facilityId,
        date: new Date().toISOString().split('T')[0],
        pageSize: 30,
        page: pageNo,
        search: physicianSearch,
      })
    );
  }, [pageNo, physicianSearch]);

  // Accumulate physician pages: the reducer replaces enterprisePhysician on each success, so
  // page 0 resets the list (initial load / new search) and later pages append, deduped by uuid.
  useEffect(() => {
    if (!isPhysicianRole || !Array.isArray(physicianContent)) return;
    setPhysicianList((prev) => {
      if (pageNo === 0) return [...physicianContent];
      const seen = new Set(prev.map((p: any) => p.uuid));
      return [...prev, ...physicianContent.filter((p: any) => !seen.has(p.uuid))];
    });
  }, [physicianContent, pageNo]);

  useEffect(() => {
    if (isPhysicianRole) return;
    dispatch(
      getAllSupervisiorByDiscipline({
        discipline: documentDiscipline,
        documentType:
          source === 'FROM_DSN'
            ? 'DAILY_SOAP_NOTE' :
            selectedDocument?.formType === 'DAILY_SOAP_NOTE_REFORM' ? 'DAILY_SOAP_NOTE' :
              source === 'OTHER_SECTION' ? selectedDocument?.formType :
              selectedDocument?.documentType.replaceAll(' ', '_').toUpperCase(),
        // THIN-7233: opt the supervisor-axis path out of the legacy form-type routing
        // that returned physicians for EVAL/RECERT. Backward-compatible: BE defaults to
        // false (legacy behavior) when this param is absent.
        forSupervisorAxis: role !== 'Physician',
        // THIN-7361: drop the document's authoring therapist from the picker — a provider
        // can supervise others but never their own work. providerId is the doc's author uuid;
        // when absent the param is omitted and the BE returns the unfiltered list (the
        // server-side ASSIGN_SUPERVISOR guard rejects a self-assignment regardless).
        excludeProviderUuid: selectedDocument?.providerId,
      })
    );
  }, []);

  const handleSelectSupervisor = () => {
    // THIN-7247: dispatch by axis instead of routing through /save-submission-data.
    // The legacy BE inferred axis from document type, which mis-assigned supervisors
    // on eval/recert (physician-signed) forms that also required supervision.
    if (source === 'FROM_DSN') {
      dispatch(
        assignSupervisorAction({
          formSubmissionUuid: dsnUuid || '',
          supervisorId: selectedData?.uuid,
        })
      );
      return;
    }
    if (isPhysicianRole) {
      dispatch(
        assignPhysicianAction({
          formSubmissionUuid: selectedDocument.uuid,
          physicianId: selectedData?.uuid,
        })
      );
    } else {
      dispatch(
        assignSupervisorAction({
          formSubmissionUuid: selectedDocument.uuid,
          supervisorId: selectedData?.uuid,
        })
      );
    }
  };

  useEffect(() => {
    if (!checkAddSupervisorResponse.isEventCalled && checkAddSupervisorResponse.response === null)
      return;
    dispatch(resetSubmissionEventAction());
    close();
  }, [checkAddSupervisorResponse]);

  return (
    <Grid sx={{ background: '#FFF', width: '725px', height: '287px' }}>
      <Grid sx={{ padding: '24px' }}>
        <Typography variant="h6">Select {`${role === 'Physician' ? 'physician' : 'supervisor'}`} to request their signature</Typography>
      </Grid>

      <Grid sx={{ padding: '24px' }}>
        <Autocomplete
          // THIN-7231: /provider/physician/list returns the Physician DTO with `physicianName`;
          // /provider/get-supervisor-by-discipline returns plain `{uuid, name}` maps. Branch the
          // label getter on role so the option text isn't blank on the physician dropdown.
          getOptionLabel={(option: any) => (isPhysicianRole ? option?.physicianName : option?.name) ?? ''}
          fullWidth
          options={options}
          onChange={(e, value) => handleChangeSupervisor(e, value)}
          onInputChange={(_e, value, reason) => handleSearchInputChange(value, reason)}
          // Physician results are searched server-side, so don't let MUI re-filter them;
          // supervisor options keep MUI's default client-side filtering.
          {...(isPhysicianRole ? { filterOptions: (x: any) => x } : {})}
          isOptionEqualToValue={(option: any, value: any) => option?.uuid === value?.uuid}
          value={selectedData}
          // Only disable while the very first page loads. Don't disable on an empty physician
          // list (a no-match search must stay editable) or during scroll-to-load-more.
          disabled={
            isPhysicianRole ? optionsLoading && options.length === 0 : optionsLoading || options.length === 0
          }
          ListboxProps={{
            style: { maxHeight: '300px', overflow: 'auto' },
            onScroll: (event: React.UIEvent<HTMLUListElement>) => {
              if (!isPhysicianRole) return;
              const node = event.currentTarget;
              if (Math.abs(node.scrollHeight - node.scrollTop - node.clientHeight) <= 1) {
                loadMorePhysicians();
              }
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              fullWidth
              label={
                !isPhysicianRole && options.length === 0
                  ? 'No Records Found'
                  : `Select ${isPhysicianRole ? 'physician' : 'supervisor'}`
              }
              name="supervisor"
            />
          )}
        />
      </Grid>

      <Grid
        container
        justifyContent={'right'}
        sx={{ paddingRight: '24px' }}
      >
        <Button
          sx={{ marginRight: '8px' }}
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelectSupervisor}
          disabled={!selectedData}
        >
          {optionsLoading ? (
            <img
              src={loader}
              style={{ height: 30 }}
              alt=''
            />
          ) : (
            'Confirm'
          )}
        </Button>
      </Grid>
    </Grid>
  );
};

export default SelectSuperVisor;
